import { Wizard } from '@vwfs-bronson/bronson-react';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { JOURNEY_STATE, useApplicationState } from '../../../App.state';
import { adobeDataLayer } from '../../../lib/adobe-analytics-data-layer/AdobeDataLayer';
import { getHeroTeaserTitle } from './journey-step-helpers';
import {
  getJourneyStatusIndex,
  JOURNEY_STEP_DEFINITIONS,
  normalizeJourneyStatus,
  WizardAnalyticsLinkName
} from './journey-step-helpers';

export interface WizardStep {
  stepName: string;
  link: string;
  isActive: boolean;
  isDone: boolean;
  isDisabled?: boolean;
  additionalPaths?: string[];
  onClick?: (step: WizardStep) => void;
}

const getWizardSteps = (t: TFunction, currentJourneyStatus: JOURNEY_STATE): WizardStep[] => {
  const currentStatusIndex = getJourneyStatusIndex(currentJourneyStatus);
  const isConfirmationStep =
    normalizeJourneyStatus(currentJourneyStatus) === JOURNEY_STATE.CONFIRMATION;

  const buildStep = (
    stepName: string,
    link: string,
    status: JOURNEY_STATE,
    analyticsLinkName: WizardAnalyticsLinkName,
    additionalPaths?: string[]
  ): WizardStep => {
    const stepIndex = getJourneyStatusIndex(status);

    return {
      stepName,
      link,
      additionalPaths,
      isActive: currentStatusIndex === stepIndex,
      isDone:
        currentStatusIndex > stepIndex ||
        (status === JOURNEY_STATE.CONFIRMATION && isConfirmationStep),
      isDisabled: isConfirmationStep && status !== JOURNEY_STATE.CONFIRMATION,
      onClick: () => adobeDataLayer.interactionWizardLinkClicked(analyticsLinkName)
    };
  };

  return JOURNEY_STEP_DEFINITIONS.map((stepDefinition) =>
    buildStep(
      t(stepDefinition.wizardLabelKey),
      stepDefinition.link,
      stepDefinition.status,
      stepDefinition.analyticsLinkName,
      stepDefinition.additionalPaths
    )
  );
};

const WizardContainer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('scaffolding');
  const {
    state: { currentJourneyStatus }
  } = useApplicationState();

  const wizardSteps = getWizardSteps(t, currentJourneyStatus);

  const activeIndex = wizardSteps.findIndex((step) => step.isActive === true);
  const activeStep = wizardSteps[activeIndex];
  const stepIndex = activeIndex + 1;
  const stepCount = wizardSteps.length;

  return (
    <Wizard
      data-wizard-collapse="m"
      collapsedText={t('wizard.collapsedTitle') || t('wizard.fallback')}
      collapsedCounter={activeStep ? `${stepIndex} / ${stepCount}` : ''}
      collapsedTitle={`${t('wizard.collapsedTitle')} ${
        activeStep ? `${stepIndex} / ${stepCount}` : ''
      }`}
      stepLabels={{
        doneLabel: t('wizard.doneLabel'),
        errorLabel: t('wizard.errorLabel'),
        notDoneLabel: t('wizard.notDoneLabel'),
        noInteractionLabel: t('wizard.noInteractionLabel')
      }}
      aria-label={t('wizard.ariaLabel')}
    >
      {wizardSteps.map((step, index) => (
        <Wizard.Item
          key={index}
          active={step.isActive}
          noInteraction={step.isDisabled}
          href={step.isActive || step.isDone ? step.link : undefined}
          done={step.isDone}
          onClick={(e) => {
            e.preventDefault();

            if (step.isDisabled) {
              return;
            }
            if (step.onClick) {
              step.onClick(step);
            }

            navigate(step.link);
          }}
        >
          {step.stepName}
        </Wizard.Item>
      ))}
    </Wizard>
  );
};

export default WizardContainer;
